import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Paper,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { Add, AutoFixHigh, Visibility } from '@mui/icons-material';
import useSnackbar from '../../../hooks/useSnackbar';
import { useCatalogEditor } from './useCatalogEditor';
import { getCatalog, seedCatalog, SbCatalog } from '../../../services/serviceBooking';

const EMPTY_CATALOG: SbCatalog = {
  jobTypes: [],
  appliances: [],
  components: [],
  attributeOptions: [],
};

/**
 * Single main table of Enquiry Types (the "main purpose"). Keys are never shown
 * — they're dev-only. Click "View" to open a dedicated screen that manages the
 * enquiry's appliances, components and the "Other" intake form.
 */
const CatalogManager: React.FC = () => {
  const navigate = useNavigate();
  const { showSnackbar, SnackbarComponent } = useSnackbar();
  const [catalog, setCatalog] = useState<SbCatalog>(EMPTY_CATALOG);
  const [loading, setLoading] = useState(true);
  const [seeding, setSeeding] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const data = await getCatalog();
      setCatalog({ ...EMPTY_CATALOG, ...data });
    } catch (err: any) {
      showSnackbar(err?.message || 'Failed to load catalog', 'error');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const { openCreate, quickToggle, togglingKey, editorElement } = useCatalogEditor(
    catalog,
    load,
    showSnackbar,
  );

  const handleSeed = async () => {
    try {
      setSeeding(true);
      const res = await seedCatalog();
      showSnackbar(
        `Seeded: ${res?.jobTypes ?? 0} enquiry types, ${res?.appliances ?? 0} equipment, ${res?.components ?? 0} components, ${res?.attributeOptions ?? 0} options`,
        'success',
      );
      await load();
    } catch (err: any) {
      showSnackbar(err?.message || 'Seed failed', 'error');
    } finally {
      setSeeding(false);
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
        <Typography variant="body2" color="text.secondary">
          Manage your enquiry types below. Open <b>View</b> on any row to configure
          its appliances, components and the “Other” intake form.
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            size="small"
            startIcon={seeding ? <CircularProgress size={16} /> : <AutoFixHigh />}
            disabled={seeding}
            onClick={handleSeed}
          >
            Seed defaults
          </Button>
          <Button variant="contained" size="small" startIcon={<Add />} onClick={() => openCreate('jobTypes')}>
            Add enquiry type
          </Button>
        </Box>
      </Box>

      <Paper variant="outlined">
        {loading ? (
          <Box sx={{ p: 6, textAlign: 'center' }}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Icon</TableCell>
                  <TableCell>Enquiry Type</TableCell>
                  <TableCell>Kind</TableCell>
                  <TableCell>Flow</TableCell>
                  <TableCell>Enabled</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {catalog.jobTypes.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 5, color: 'text.secondary' }}>
                      Nothing yet. Click “Seed defaults” to load the standard catalog, or “Add enquiry type”.
                    </TableCell>
                  </TableRow>
                ) : (
                  catalog.jobTypes.map((row) => {
                    const rowKey = row._id || row.key;
                    const isPrimary = row.group !== 'secondary';
                    const applianceCount = catalog.appliances.filter(
                      (a) => !a.supportedJobTypeKeys?.length || a.supportedJobTypeKeys.includes(row.key),
                    ).length;
                    return (
                      <TableRow key={rowKey} hover sx={{ opacity: row.isActive === false ? 0.55 : 1 }}>
                        <TableCell>
                          <Avatar src={row.icon || undefined} variant="rounded" sx={{ width: 38, height: 38 }}>
                            {(row.label || '?')[0]}
                          </Avatar>
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>{row.label}</TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            color={isPrimary ? 'primary' : 'info'}
                            label={isPrimary ? 'Booking-based' : 'Request-based'}
                          />
                        </TableCell>
                        <TableCell sx={{ color: 'text.secondary', fontSize: '0.82rem' }}>
                          {isPrimary ? `${applianceCount} equipment` : 'Separate flow'}
                        </TableCell>
                        <TableCell>
                          {togglingKey === rowKey ? (
                            <CircularProgress size={16} />
                          ) : (
                            <Tooltip title={row.isActive === false ? 'Disabled' : 'Enabled'}>
                              <Switch
                                size="small"
                                checked={row.isActive !== false}
                                onChange={(e) => quickToggle('jobTypes', row, e.target.checked)}
                              />
                            </Tooltip>
                          )}
                        </TableCell>
                        <TableCell align="right">
                          <Tooltip title="View & manage">
                            <IconButton
                              size="small"
                              onClick={() => navigate(`/manage/services/enquiry/${row.key}`)}
                              sx={{ color: '#A855F7' }}
                            >
                              <Visibility fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {editorElement}
      {SnackbarComponent}
    </Box>
  );
};

export default CatalogManager;
